import type { RouteLocationNormalized, Router } from 'vue-router'

export interface BreadcrumbItem {
  title: string
  name?: string
  path?: string
  params?: Record<string, any>
  icon?: string
}

interface BreadcrumbConfig {
  parent?: string[]
  title?: string | ((route: RouteLocationNormalized) => string)
  params?: string[]
}

/**
 * Generate breadcrumb items from current route
 */
export function generateBreadcrumb(
  route: RouteLocationNormalized,
  router: Router,
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = []

  // Type guard for breadcrumb config
  const routeMeta = route.meta as any
  const currentBreadcrumb = routeMeta?.breadcrumb as BreadcrumbConfig | undefined

  if (!currentBreadcrumb) {
    // Fallback to route title if no breadcrumb config
    return [{
      title: String(routeMeta?.title || route.name || 'Untitled'),
      name: String(route.name),
      path: route.path,
      params: route.params,
    }]
  }

  // Build breadcrumb chain from parent routes
  const parentRoutes = currentBreadcrumb.parent || []

  for (const parentRouteName of parentRoutes) {
    const parentRoute = router.getRoutes().find(r => r.name === parentRouteName)
    if (parentRoute) {
      const parentMeta = parentRoute.meta as any
      const parentBreadcrumb = parentMeta?.breadcrumb as BreadcrumbConfig | undefined
      const parentTitle = parentBreadcrumb?.title || parentMeta?.title || parentRouteName

      breadcrumbs.push({
        title: typeof parentTitle === 'function' ? parentTitle(route) : String(parentTitle),
        name: parentRouteName,
        path: generateRoutePath(parentRoute.path, route.params, parentBreadcrumb?.params),
      })
    }
  }

  // Add current route
  const currentTitle = currentBreadcrumb.title || routeMeta?.title || route.name || 'Untitled'
  breadcrumbs.push({
    title: typeof currentTitle === 'function' ? currentTitle(route) : String(currentTitle),
    name: String(route.name),
    path: route.path,
    params: route.params,
  })

  return breadcrumbs
}

/**
 * Generate route path with params
 */
function generateRoutePath(
  routePath: string,
  currentParams: Record<string, any>,
  requiredParams?: string[],
): string {
  let path = routePath

  // Replace route parameters with actual values
  if (requiredParams) {
    for (const param of requiredParams) {
      if (currentParams[param])
        path = path.replace(`:${param}`, String(currentParams[param]))
    }
  }
  else {
    // Replace all params
    Object.entries(currentParams).forEach(([key, value]) => {
      path = path.replace(`:${key}`, String(value))
    })
  }

  return path
}

/**
 * Get breadcrumb title with proper formatting
 */
export function getBreadcrumbTitle(
  title: string | ((route: RouteLocationNormalized) => string),
  route: RouteLocationNormalized,
): string {
  if (typeof title === 'function')
    return title(route)

  return title
}

/**
 * Check if breadcrumb item is active (current page)
 */
export function isBreadcrumbActive(
  item: BreadcrumbItem,
  currentRoute: RouteLocationNormalized,
): boolean {
  return item.name === String(currentRoute.name)
}

/**
 * Format breadcrumb item for display
 */
export function formatBreadcrumbItem(
  item: BreadcrumbItem,
  isLast = false,
): BreadcrumbItem {
  return {
    ...item,
    title: truncateTitle(item.title, isLast ? 50 : 30),
  }
}

/**
 * Truncate title if too long
 */
function truncateTitle(title: string, maxLength: number): string {
  if (title.length <= maxLength)
    return title

  return `${title.substring(0, maxLength)}...`
}

/**
 * Get breadcrumb navigation data for specific groups
 */
// export function getBreadcrumbByGroup(
//   group: string,
//   route: RouteLocationNormalized,
// ): BreadcrumbItem[] {
//   const groupMappings: Record<string, BreadcrumbItem[]> = {
//     'agent-management': [
//       { title: 'Dashboard', name: 'dashboard', path: '/agent-management' },
//     ],
//     'agent-store': [
//       { title: 'Agent Store', name: 'agent-store', path: '/agent-store' },
//     ],
//     'knowledge': [
//       { title: 'Knowledge Base', name: 'knowledge', path: '/knowledge' },
//     ],
//     'tasks': [
//       { title: 'Tasks', name: 'tasks', path: '/tasks' },
//     ],
//     'settings': [
//       { title: 'Settings', name: 'workspace-settings', path: '/workspace-settings' },
//     ],
//   }

//   return groupMappings[group] || []
// }
